import type { InteractionSubmission, LifeState } from '../domain/life-state'
import { parseLifeState } from '../domain/life-state'

const REQUEST_TIMEOUT_MS = 8_000

export interface InteractionResult {
  accepted: boolean
  acceptedWeight: number
  stateVersion: number
}

export class LifeClientError extends Error {
  constructor(
    message: string,
    readonly retryable: boolean,
    readonly status?: number,
  ) {
    super(message)
    this.name = 'LifeClientError'
  }
}

export class LifeClient {
  constructor(
    private readonly baseUrl = '',
    private readonly fetcher: typeof fetch = fetch,
  ) {}

  async getState(): Promise<LifeState> {
    const response = await this.request('/v1/life/state', {
      method: 'GET',
    })
    try {
      return parseLifeState(await response.json())
    } catch (error) {
      if (error instanceof LifeClientError) {
        throw error
      }
      throw new LifeClientError('invalid_life_state_response', false)
    }
  }

  async submit(submission: InteractionSubmission): Promise<InteractionResult> {
    const response = await this.request('/v1/life/interactions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(submission),
    })
    const value = await response.json() as Partial<InteractionResult>
    if (
      typeof value.accepted !== 'boolean'
      || typeof value.acceptedWeight !== 'number'
      || !Number.isFinite(value.acceptedWeight)
      || !Number.isInteger(value.stateVersion)
      || (value.stateVersion ?? -1) < 0
    ) {
      throw new LifeClientError('invalid_interaction_response', false)
    }
    return value as InteractionResult
  }

  private async request(path: string, init: RequestInit): Promise<Response> {
    let response: Response
    try {
      response = await this.fetcher.call(
        globalThis,
        `${this.baseUrl}${path}`,
        {
          ...init,
          credentials: 'omit',
          cache: 'no-store',
          signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
        },
      )
    } catch (error) {
      if (error instanceof LifeClientError) {
        throw error
      }
      throw new LifeClientError('life_network_unavailable', true)
    }

    if (!response.ok) {
      throw new LifeClientError(
        `life_request_failed:${response.status}`,
        response.status === 429 || response.status >= 500,
        response.status,
      )
    }
    return response
  }
}
